import { dateToDateValue, dateValueToDate } from "@smartsuite/fields-logic/lib/utils/date";
import type { DueDateCalendarDateValue } from "@smartsuite/react-ui/lib";
import type { DueDateValue } from "@smartsuite/types";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import { formatInTimeZone } from "date-fns-tz";

export const defaultDueDateValue = {
  from_date: {
    date: "",
    include_time: false,
  },
  to_date: {
    date: "",
    include_time: false,
  },
};

export const getDueDateValueString = (
  includeTime: boolean,
  date: string,
  timeFormat: string,
  timezone?: string,
): string => {
  const parsedDate = new Date(date);
  const timeFormatPart = timeFormat === "12h" ? "hh:mm a" : "HH:mm";

  if (!includeTime) {
    return format(parsedDate, "MMMM dd, yyyy");
  }

  // Internal forms show the value in the user's profile timezone (not the browser's) with the
  // "(GMT±HH:MM)" offset. Without a timezone the legacy browser-local behaviour is preserved.
  return timezone
    ? formatInTimeZone(parsedDate, timezone, `MMMM dd, yyyy, ${timeFormatPart} (OOOO)`)
    : format(parsedDate, `MMMM dd, yyyy, ${timeFormatPart} (zzzz)`);
};

export const combineDateAndTime = (
  dateString: string,
  timeString: string,
  timeFormat: string,
  timezone?: string,
): string => {
  if (!dateString) return "";
  if (!timeString) return format(new Date(dateString), "yyyy-MM-dd'T'HH:mm:ss");

  const date = parse(dateString, "MMMM dd, yyyy", new Date());
  const timeDate = parse(timeString, timeFormat === "24h" ? "HH:mm" : "hh:mm a", new Date());
  if (Number.isNaN(date.getTime()) || Number.isNaN(timeDate.getTime())) {
    return "";
  }

  const combinedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    timeDate.getHours(),
    timeDate.getMinutes(),
  );

  // Internal forms interpret the picked wall-clock as the user's profile timezone and store the
  // matching UTC instant; without a timezone the legacy browser-local ISO (no offset) is kept.
  return timezone
    ? (dateToDateValue(combinedDate, true, timezone).date ?? "")
    : format(combinedDate, "yyyy-MM-dd'T'HH:mm:ss");
};

export const getTimeFromDateString = (dateString: string, timeFormat: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return format(date, timeFormat === "24h" ? "HH:mm" : "hh:mm a");
};

export const getDefaultValueState = (value: DueDateValue): DueDateValue => {
  if (!value?.to_date?.date && !value?.from_date?.date) {
    return defaultDueDateValue;
  }
  return {
    from_date: value.from_date.date ? value.from_date : defaultDueDateValue.from_date,
    to_date: value.to_date.date ? value.to_date : defaultDueDateValue.to_date,
  };
};

export const getFormattedInputDate = (date: Date): string => {
  if (isValid(date)) {
    return format(date, "dd/MM/yyyy");
  }
  return "";
};

// A with-time boundary in an internal form must be resolved in the profile timezone so the picker
// Date, the time field and the calendar day all reflect the user's timezone. Date-only boundaries
// (and the no-timezone/legacy case) stay on the browser-local path unchanged.
const boundaryToPickerDate = (
  boundary: DueDateValue["from_date"],
  timezone?: string,
): Date | null => {
  if (!boundary.date) return null;

  return timezone && boundary.include_time
    ? dateValueToDate({ date: boundary.date, include_time: true }, timezone)
    : new Date(boundary.date);
};

const boundaryTimeValue = (
  boundary: DueDateValue["from_date"],
  timeFormat: string,
  timezone?: string,
): string => {
  if (!boundary.include_time || !boundary.date) return "";

  return timezone
    ? formatInTimeZone(
        new Date(boundary.date),
        timezone,
        timeFormat === "24h" ? "HH:mm" : "hh:mm a",
      )
    : getTimeFromDateString(boundary.date, timeFormat);
};

const boundaryCalendarDate = (boundary: DueDateValue["from_date"], timezone?: string): string => {
  if (!boundary.date) return "";

  return timezone && boundary.include_time
    ? formatInTimeZone(new Date(boundary.date), timezone, "MMM dd, yyyy")
    : format(new Date(boundary.date), "MMM dd, yyyy");
};

export const initDatesFromValue = (
  value: DueDateValue,
  timeFormat: string,
  timezone?: string,
): {
  fromDate: Date | null;
  endDate: Date | null;
  timeValues: DueDateCalendarDateValue;
  calendarDateValues: DueDateValue;
} => {
  const fromDateParsed = boundaryToPickerDate(value.from_date, timezone);
  const endDateParsed = boundaryToPickerDate(value.to_date, timezone);
  const resolvedFromDate = value.from_date.date ? fromDateParsed : endDateParsed;

  return {
    fromDate: value.from_date.date ? resolvedFromDate : null,
    endDate: endDateParsed,
    timeValues: {
      from_date: boundaryTimeValue(value.from_date, timeFormat, timezone),
      to_date: boundaryTimeValue(value.to_date, timeFormat, timezone),
    },
    calendarDateValues: {
      from_date: {
        date: boundaryCalendarDate(value.from_date, timezone),
        include_time: value.from_date.include_time,
      },
      to_date: {
        date: boundaryCalendarDate(value.to_date, timezone),
        include_time: value.to_date.include_time,
      },
    },
  };
};
